<template>
	<div class="map_main">
		<gmap-map ref="map"
			:center="center"
			:zoom="zoom"
			:fitBounds="fitBounds"
			style="width: 100%; height: 100%;">
			<div v-for="(markertypes, markertypesindex) in marker_list" v-bind:key="markertypesindex">
				<gmap-marker ref="gmapmarker" :key="index"
					v-for="(marker, index) in marker_list[markertypesindex]"
					:position="marker.position"
					:label="marker.label"
					:icon="marker.icon"
					:opacity="marker.opacity"
					:visible="marker.visible"
					:animation="marker.animation"
					:clickable="marker.clickable"
					:draggable="marker.draggable"
					@drag="marker_drag"
					@dragend="marker_dragend"
					@dragstart="marker_dragstart"
					@click="info_window(marker, index)">
				</gmap-marker>
			</div>
			<div v-for="destination in polygon" v-bind:key="destination">
				<gmap-polygon
					:options="{
						fillColor: '#05C2FF',
						fillOpacity: 0.5,
						strokeColor: '#338DFF',
						strokeOpacity: 0.8,
						strokeWeight: 5
					}"
					:editable="destination.Edit"
					:draggable="destination.Edit"
					:paths="destination.Path"
					@paths_changed="polygonPathUpdate($event)"
					@dragend="destination_dragend"
				></gmap-polygon>
			</div>
			<gmap-info-window :position="info_pos"
				:opened="info_show"
				:options="info_options"
				@closeclick="info_show = false">
				<div class="info_main" v-for="(mInfo, mI) in info_currentmarker.info" v-bind:key="mI">
					<div v-if="mInfo.type == 'string'">
						<div class="info_title" :style="mInfo.style">{{mInfo.title}}</div>
						<div class="separator"></div>
						<div class="info_value" :style="mInfo.style">{{mInfo.string}}</div>
					</div>
					<button
						class="info_controls"
						v-if="mInfo.type == 'button'"
						@click="mapsButtonClick(info_currentmarker)"
					>{{mInfo.title}}</button>
				</div>
			</gmap-info-window>
		</gmap-map>
	</div>
</template>

<script>
/* eslint-disable */
/* NOTES -------------------- IMPORTANT
    Markers (marker_list) takes in arrays containing the markers, eg
    marker_list: [
      [
        {marker1},
        {marker2},
        {marker3},
      ],           <------ first set of markers
      [
        {marker1},
        {marker2},
        {marker3},
      ],           <------ second set of markers
    ]

    EVERY set of markers have to be in an ARRAY in marker_list, even if its one.
    This allows flexibility for grouping markers but in order to update them,
    please use:

    this.$set(marker_list, index, <array of markers>);

    $set has to be used because unfortunately, for now, Vue JS does not detect direct changes in arrays for indexes, eg

    array[1] = 'hello';   <--------- this is not detected thus will not update map with markers.


    Info Boxes take in 3 types of data,

      - String
      - Object
      - Array (of objects)

    Example, given in marker objects (info is the string u want to put)

      { position: {}, info: 'Hello' },                       <--------- String

      { position: {}, info: { title: 'Area', string: 'Glenmarie' } },        <--------- Object

      { position: {}, info: [                            <--------- Array
        { title: 'Name', string: 'DSM' },
        { title: 'Open?', string: 'Yes' },
        { title: 'Area', string: 'Glenmarie' },
      ] },

  */

var directionService;
var directionRenderer;
var map;

//Fixed Variables
const defaultpos = { lat: 3.091033, lng: 101.403 };
const defaultzoom = 10;

export default {
	name: 'GoogleMap',

	components: {
	},

	data: function() { 
		return {
			map_googleloaded: false, //This is a boolean to show if google map has been loaded (some functions will return google not defined if this hasn't loaded)
		}
	},

	props: {
		info_show: { type: Boolean, default: false }, //This is a boolean to hide/show the Infowindow
		info_content: { type: String, default: "" }, //This is the content of the infowindow (may not be in use)
		info_currentmarker: {
			type: Object,
			default: function() {
				return {};
			}
		}, //This will be set when a marker is clicked (to retrieve information on that particular marker)

		//Public Properties ------------------
		//Google Maps stuff
		center: {
			type: Object,
			default: function() {
				return defaultpos;
			}
		}, //Center point of the map (will move the whole map till the center of it is at this LatLng)
		zoom: { type: Number, default: defaultzoom }, //Zoom level of the map (Different level ranges will show stuff like buildings etc)
		fitBounds: {
			type: Object,
			default: function() {
				return {};
			}
		}, //This sets the maps fitting boundaries (Fits the screen to specified boundary, please see Google Map API, it is quite complex)

		//Google Maps Destinations stuff
		direction_route: {
			type: Object,
			default: function() {
				return { fromlat: 0, fromlng: 0, tolat: 0, tolng: 0 };
			}
		}, //This will show the direction from point A to B, read on Direction Service by Google

		//Google Marker stuffs
		marker_list: {
			type: Array,
			default: function() {
				return [];
			}
		}, //This is the list that holds the markers, Instructions are above with some examples

		//Google Infowindow stuffs
		info_pos: {
			type: Object,
			default: function() {
				return {};
			}
		}, //This is the position of the Infowindow when you click on markers
		info_options: {
			type: Object,
			default: function() {
				return { pixelOffset: { width: 0, height: -50 } };
			}
		}, //This is the options for the infowindow (this is set to make it just nicely above markers)

		//Google Polygons/Geofence
		polygon: {
			type: Array,
			default: function() {
				return [];
			}
		}, //Holds polygon/geofence, format: [{Path: [geofence array], Edit: true/false},{Path: [geofence array], Edit: true/false}]
		//polygonStrokeColor: { type: String, default: '#338DFF' },

		// If user passes in an address string, maps marker and position will default to that address
		address: {
			type: String,
			default: function() {
				return ''
			}
		}

		// Public events
		// @drag - fired when marker is dragged, returns marker object
		// @dragend - fired when marker drag has ended, returns marker object
		// @dragstart - fired when marker drag started, returns marker object
		// @mapInfoButtonClick - fired when a BUTTON in the marker is clicked returns the marker object
	},

	async mounted() {
		await this.$gmapApiPromiseLazy().then(() => {
			directionService = new google.maps.DirectionsService();
			directionRenderer = new google.maps.DirectionsRenderer();
			map = this.$refs.map.$mapObject;
			this.map_googleloaded = true;
		}).catch(err => {
			// throw ("[GM_MOUNTED] ", err);
		});

				console.log(this.marker_list);
				if(this.marker_list.length == 0) return
				let lat = this.marker_list[0][0].position.lat;
				let lng = this.marker_list[0][0].position.lng;

			// if lat and lng is undefined
			if((!this.hasValue(lat) || !this.hasValue(lng)) || (lat == 0 && lng == 0 )){
				this.$emit('alert', 'Your coordinates shows no result, please press "Show on map" to move map to your inputted address instead.' )
				this.$emit('newAddr', {fullAddr: "Not found"})
			// Will try to get coords based off address, if not found, coords will be used via given lat and lng
			// await this.addrToCoords(this.address)
			} else {
				await this.coordsToAddr(lat, lng)
			}
		



	},

	methods: {
		// Marker functions
		marker_drag: function(marker) {
			this.$emit("drag", marker);
			this.info_pos = { lat: marker.latLng.lat(), lng: marker.latLng.lng() };
		},
		marker_dragend: async function(marker) {
			await this.coordsToAddr(marker.latLng.lat(), marker.latLng.lng())
			this.$emit("dragend", marker);
		},
		marker_dragstart: function(marker) {
			this.$emit("dragstart", marker);
		},
		info_window: function(marker, index) {
			var markerobject = this.$refs.gmapmarker[index].$markerObject;
			if(!markerobject) return
			if (marker.draggable) {
				this.info_pos = {
					lat: markerobject.position.lat(),
					lng: markerobject.position.lng()
				};
			} else {
				this.info_pos = marker.position;
			}
			if (!(marker.info == "" || marker.info == null)) {
				this.info_show = true;
			}
			this.info_currentmarker = marker;
			//if (typeof marker.info == 'string') {
			//  this.info_content += marker.info;
			//} else if (Array.isArray(marker.info)) {
			//  for (var i = 0; i < marker.info.length; i++) {
			//    this.info_content += `<div class='info_main'> <div class='info_contentbox'>  <div class='info_title'>${marker.info[i].title}</div>  <div class='info_value'>${marker.info[i].string}</div>  </div> </div>`;
			//  }
			//} else if (typeof marker.info == 'object') {
			//  this.info_content += `<div class='info_main'> <div class='info_contentbox'>  <div class='info_title'>${marker.info.title}</div>  <div class='info_value'>${marker.info.string}</div>  </div> </div>`;
			//}
		},
		mapsButtonClick: function(marker) {
			this.$emit("mapInfoButtonClick", marker);
		},
		//Polygon functions
		destination_dragend: function(polygon){
			this.$emit("polyDragend", polygon);
		},
		polygonPathUpdate(mvcArray) {
			var polyPaths = []
			for (let i = 0; i < mvcArray.getLength(); i++) {
				for (let j = 0; j < mvcArray.getAt(i).getLength(); j++) {
					var point = {
						lat: mvcArray.getAt(i).getAt(j).lat(),
						lng: mvcArray.getAt(i).getAt(j).lng()
					};
					polyPaths.push(point);
				}
			}
			this.$emit("polyPathUpdate", polyPaths)
		},
		// Directions Service functions
		map_showDirections: function(FromLat, FromLng, ToLat, ToLng) {
			if (this.map_googleloaded == true) {
				directionRenderer.setMap(map);
				var request = {
					origin: { lat: parseFloat(FromLat), lng: parseFloat(FromLng) },
					destination: { lat: parseFloat(ToLat), lng: parseFloat(ToLng) },
					travelMode: "DRIVING"
				};
				directionService.route(request, function(response, status) {
					if (status == "OK") {
						directionRenderer.setDirections(response);
					} else {
						alert("Google Maps: " + status);
					}
				});
			}
		},
		hasValue(value){
			return !isNaN(value) && typeof(value) != 'undefined' && value != null
		},
		// Converting address to coordinates using geocoder
		async addrToCoords(address) {

				if(!address) return

				try	{
					let coords = await this.geoCode(address)
					this.$emit("moveMarker", coords);
					this.marker_list = [coords];
					this.info_pos = coords
					await this.coordsToAddr(coords.lat, coords.lng)

				// If nothing or there is an error, go for default (Company location)
				} catch(err) { 
					try {
						this.$emit('alert', "Address was not found, using your current location")
						let coords = await this.getDevCoords()
						this.$emit("moveMarker", coords);
						this.marker_list = [coords];
						this.info_pos = coords
						await this.coordsToAddr(coords.lat, coords.lng)
					} catch (err) {
						this.$emit('alert', "Address was not found, searching for company's address")
						let address = (await this.$apiCallV2('/Login/GetDetails'))['CoName']
						// Catch err here to signify that even their company could not be found on maps
						let coords = await this.geoCode(address).catch((err) => {
							// Absolute default where it would not fail? (For now it uses passed in longitute an latitude)
							console.log("[DCTA]", err)
						})
						this.$emit("moveMarker", coords);
						this.marker_list = [coords];
						this.info_pos = coords
						await this.coordsToAddr(coords.lat, coords.lng)
					}







				}

		},

		getDevCoords(){
			return new Promise((Resolve, Reject) => {
				if(navigator.geolocation){
					navigator.geolocation.getCurrentPosition((function (position){
						Resolve({
							lat: position.coords.latitude,
							lng: position.coords.longitude
						})
					}), Reject)
				} else {
					Reject("Browser does not support location tracking")
				}
			})
		},

		async coordsToAddr(lat, long){
			if(lat === 0 && lng === 0) return
			let latLng = { lat: parseFloat(lat), lng: parseFloat(long) }
			let address = await this.geoCodeReverse(latLng).catch( err => {
				// console.log("[CTA]", err)
				if(err == "ZERO_RESULTS"){
					this.$emit('alert', 'Your coordinates shows no result, please press "Show on map" to move map to your inputted address instead.' )
				}
			})
			this.$emit('newAddr', address)
		},

		async geoCode(address){
			// console.log("running geocode")
			return new Promise((Resolve, Reject) => {
					let geoObj = new google.maps.Geocoder();
					geoObj.geocode({address}, function(res, status){
						if(status == google.maps.GeocoderStatus.OK) {
							let location = res[0].geometry.location
							map.setCenter(location)
							Resolve({
								lat: location.lat(),
								lng: location.lng()
							})
						} else {
							Reject(status)
						}
					})})
		},

		async geoCodeReverse(location){
			return new Promise((Resolve, Reject) => {
				let geoObj = new google.maps.Geocoder();
				geoObj.geocode({location}, function(res, status) {
					if(status == google.maps.GeocoderStatus.OK){
							if(res.length == 0) return Reject("No address was found")
							// We only need one address, get the most top.
							let addrMapping = {
								no: 'street_number',
								street: 'route',
								street2: 'sublocality',
								state: 'administrative_area_level_1',
								city: 'locality',
								country: 'country',
								postcode: 'postal_code'
							}
							let gAddrComp = res[0].address_components
							let addrComp = {}

						// Populate object
						for(let key in addrMapping){
							addrComp[key] = ''
						}
						
						for(let comp of gAddrComp){
							for(let item in addrMapping){
								if(comp['types'].includes(addrMapping[item])){
									addrComp[item] += (" " + comp["long_name"])
 								}
							}
						}
						
						addrComp.fullAddr = res[0]["formatted_address"]
						return Resolve(addrComp)
						}
					return Reject(status)
				})
			})
		},

		// Public method to send the map as an object
		getMapObject: function() {
			return map;
		}
	},

	watch: {

		marker_list: async function(markers) {

			if(markers.length == 0) return

			if (markers.length <= 0) {
				this.info_show = false;
			} 
			let lat = markers[0][0].position.lat;
			let lng = markers[0][0].position.lng;

			if((!this.hasValue(lat) || !this.hasValue(lng)) || (lat == 0 && lng == 0 )){
				this.$emit('alert', 'Your coordinates shows no result, please press "Show your address on map" button to move map to your inputted address instead.' )
			}

			// else{
			// 	await this.coordsToAddr(markers[0][0].position.lat, markers[0][0].position.lng)
			// }

			// if(!this.hasValue(markers[0][0].position.lat), !this.hasValue(markers[0][0].position.lng)){
			// 	await this.addrToCoords(this.address)
			// } 

		},
		direction_route: function(route) {
			if (
				route.fromlat == 0 ||
				route.fromlng == 0 ||
				route.tolat == 0 ||
				route.tolng == 0
			) {
				if (this.map_googleloaded == true) {
					directionRenderer.setDirections({ routes: [] });
				}

				map.setCenter(defaultpos);
				map.setZoom(defaultzoom);
			} else {
				this.map_showDirections(
					route.fromlat,
					route.fromlng,
					route.tolat,
					route.tolng
				);
			}
		},
		fitBounds: function(latlngbounds) {

			if(!latlngbounds) return
			try {
				var coordObj = new google.maps.LatLngBounds();
				for (var i = 0; i < latlngbounds.length; i++) {
					coordObj.extend(new google.maps.LatLng(latlngbounds[i]['lat'],latlngbounds[i]['lng']));
				}
				map.fitBounds(coordObj);
			} catch (error) {
			}
		}
	}
};
</script>

<style lang="scss">
@import "@/css/all.scss";

.map_main {
	width: 100%;
	height: 100%;
}

.info_main {
	width: auto;
	display: flex;
	flex-direction: column;
}

.info_contentbox {
	width: 100%;
	height: auto;
	display: flex;
	justify-content: space-between;
}

.info_title {
	width: 100%;
	display: flex;
	font-size: 10px;
	align-items: center;
	justify-content: flex-start;
	font-weight: bold;
}

.info_value {
	width: 65%;
	min-width: 30%;
	padding: 0.2% 0 0.2% 0;
	font-size: 10px;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	overflow: hidden;
}

.info_controls {
	width: auto;
	display: flex;
	align-items: center;
	justify-content: center;
	align-self: flex-end;
}

.separator {
  height: 3px;
}
</style>